// Écran d'introduction questionnaire — affiche avant la première question
// Structure : Card contexte + grille features + CTA

import { ClipboardList, MessageSquarePlus, Save, Clock, HelpCircle } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { trackEvent } from '../../lib/analytics'

export interface QuestionnaireIntroProps {
  title: string
  subtitle: React.ReactNode
  metierNom?: string
  communLabel?: string
  questionCount: number
  onStart: () => void
}

export default function QuestionnaireIntro({
  title,
  subtitle,
  metierNom,
  communLabel,
  questionCount,
  onStart,
}: QuestionnaireIntroProps) {
  const estimatedMinutes = Math.max(5, Math.round(questionCount * 0.4))

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        {/* BLOC 1 : Contexte — qui, quoi, combien */}
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            {/* Icone */}
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <ClipboardList className="size-6 text-primary" />
            </div>

            {/* Titre */}
            <div>
              <h1 className="text-xl font-bold">{title}</h1>
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            </div>

            {/* Métier évalué (Q1) */}
            {metierNom && (
              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">Métier évalué</p>
                <p className="font-semibold mt-0.5">{metierNom}</p>
              </div>
            )}

            {/* Label commun (Q2) */}
            {communLabel && !metierNom && (
              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">{communLabel}</p>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <HelpCircle className="size-3.5" />
                {questionCount} questions
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="size-3.5" />
                ~{estimatedMinutes} min
              </span>
            </div>
          </CardContent>
        </Card>

        {/* BLOC 2 : Comment ca marche — 3 features */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center space-y-2">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto">
              <ClipboardList className="size-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              1 question par écran
            </p>
          </div>

          <div className="text-center space-y-2">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto">
              <MessageSquarePlus className="size-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Ajoutez des notes librement
            </p>
          </div>

          <div className="text-center space-y-2">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto">
              <Save className="size-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Sauvegarde automatique
            </p>
          </div>
        </div>

        {/* BLOC 3 : CTA */}
        <div className="text-center">
          <Button size="lg" onClick={() => { trackEvent('demarrer le questionnaire'); onStart() }} className="px-8">
            Démarrer le questionnaire
          </Button>
        </div>
      </div>
    </div>
  )
}
